import React from "react";
import Image from "next/image";

/**
 * "Tailor-made For Your Needs" — the artwork is a configurator: three rows of
 * option cards (REAL WORLD SIMULATION / PLCS / SIZE) wired to the wheel on the
 * right. So it animates as someone configuring it: a cursor works down the rows,
 * every card it passes over swells and turns the artwork's red, and the one it
 * clicks in each row STAYS red — so the three picks accumulate into a finished
 * configuration, which the cycle holds for a beat before starting over.
 *
 * Red is a state here, not a property. The artwork ships with one card per row
 * painted red as though already chosen; all twelve are rebuilt idle instead, so
 * red only ever means "under the cursor" or "picked in this pass".
 *
 * That needs the cards to be recolourable, and the Figma export is a flat bitmap
 * (its .svg is only a base64 PNG). `scripts/extract-tailor-made-layers.mjs` cuts
 * all twelve cards out of the artwork and lifts their line-art and labels into one
 * alpha mask, so each card is rebuilt here out of CSS layers — body, label strip,
 * icon ink, label ink, and the red LED detail inside a few icons — over
 * `tailor-made-base.png`, which keeps the row titles, arrows, connectors, and the
 * dial's hub. That same script also lifts the dial's rings onto the three layers
 * drawn below, so the base holds none of them.
 *
 * Geometry below is in source-artwork units (4096x1795, as measured by that
 * script) and converted to percentages, so it holds at whatever size the card
 * renders. Every layer is a full-artwork box clipped to its own region: that way
 * the one mask file serves all twelve cards without any per-card cropping.
 *
 * The stylesheet is built here and injected as plain CSS rather than through
 * styled-jsx, because styled-jsx drops keyframe blocks whose percentage selectors
 * are interpolated — the beats below would collapse to their first and last frame.
 */
const ART = { w: 4096, h: 1795 };
/** Card box: 322 wide, label strip starts 337 down, 32px chamfer bottom-right. */
const CARD_W = 322;
const SPLIT = 337;
const NOTCH = 32;
const COLS = [104, 497, 890, 1283];
const ROWS = [
  { y: 81, h: 415 },
  { y: 731, h: 416 },
  { y: 1380, h: 415 },
];
const MASK = "/images/OTSecurity/cyber-range/tailor-made-ink.png";
const ACCENT_MASK = "/images/OTSecurity/cyber-range/tailor-made-accent.png";
const CURSOR = "/images/mouse-pointer.svg";

/** The rendered artwork box, which the wheel geometry below is a share of. */
const BOX = { w: 1600, h: 701 };

/**
 * The dial on the right reads as an instrument, so it is adjusted rather than spun:
 * each ring rocks one way then the other, holding briefly at both ends.
 * `scripts/extract-tailor-made-layers.mjs` lifts the rings off the bitmap in three
 * bands whose edges fall in radii holding no pixels, and leaves the hub disc in the
 * base. Turn is half the sweep and its sign is the direction, so neighbouring bands
 * counter-rotate; the periods are mutually prime-ish so they never line up. The
 * artwork renders about 1600 units into ~600px, so anything under ~15deg reads as
 * nothing moving at all.
 */
const WHEEL_ORIGIN = "76.938% 49.929%";
const WHEEL_RINGS = [
  { name: "inner", turn: "16deg", dur: "9s", delay: "0s" },
  { name: "mid", turn: "-24deg", dur: "7.5s", delay: "-2.1s" },
  { name: "outer", turn: "34deg", dur: "11s", delay: "-4.4s" },
] as const;

/**
 * The lone grey arc up to the right of the dial sweeps too, but it is not a dial ring:
 * fitting its two edges puts its own centre at 1306.75, 306.55 — 88px away from the
 * dial's — so it turns on that pivot instead, or it would swing bodily sideways.
 *
 * It also sweeps ONE WAY only. The canvas chopped it: its circle wants to reach x 1602
 * while the artwork ends at 1599, leaving 33px of flat vertical cut on the last two
 * columns. Rotating anticlockwise drags that cut out into open space at anything past
 * -2deg; clockwise never exposes a pixel of it and merely tucks ink further behind the
 * frame edge where the arc is already cut — 4.3% of it at the +14deg used here.
 */
const ARC = { origin: "81.672% 43.730%", turn: "14deg", dur: "13s", delay: "-3s" };

/**
 * The artwork's own colours, sampled by the extract script. `accent` is the red of
 * the LEDs drawn inside the rack icons — artwork detail, not selection state, so it
 * rests red and only goes white with the rest of the ink.
 */
const IDLE = { body: "#3c4451", strip: "#5f606c", icon: "#14181d", text: "#14181d", accent: "#ec1e3c" };
const ACTIVE = { body: "#ec1e3c", strip: "#1f2327", icon: "#ffffff", text: "#ec1e3c" };

/** The cursor works one row at a time, left to right. */
const STOPS = ROWS.flatMap((_, r) => COLS.map((_, c) => ({ r, c })));

/**
 * Column the cursor clicks in each row, matching the design frame: the first card
 * of REAL WORLD SIMULATION and of PLCS, and the third of SIZE. A pick holds its red
 * for the rest of the cycle, so by the end all three are lit at once.
 */
const PICKS = [0, 0, 2];
const pickBeat = (row: number) => row * COLS.length + PICKS[row];

/**
 * One stop per beat, then a tail with no cursor that holds the finished
 * configuration. `STEP` is one beat as a share of the cycle; RISE/HOLD/FALL are
 * points inside a beat — land, dwell, release.
 */
const DURATION = 17;
const TAIL = 14;
const STEP = (100 - TAIL) / STOPS.length;
const RISE = STEP * 0.17;
const HOLD = STEP * 0.6;
const FALL = STEP * 0.79;
/** How long the three picks take to let go at the wrap, so the loop has no blink. */
const RELEASE = 3;
/** The cursor is parked on the last card until the tail has faded it out. */
const LAST = 100 - TAIL;

const pct = (v: number, total: number) => `${((v / total) * 100).toFixed(3)}%`;
const x = (v: number) => pct(v, ART.w);
const y = (v: number) => pct(v, ART.h);
const at = (v: number) => `${v.toFixed(3)}%`;

/** Rect covering the card's icon area, in the full-artwork box. */
const bodyClip = (col: number, row: (typeof ROWS)[number]) =>
  `inset(${y(row.y)} ${x(ART.w - col - CARD_W)} ${y(ART.h - row.y - SPLIT)} ${x(col)})`;

/** The label strip, chamfered at the bottom-right corner like the artwork. */
const stripClip = (col: number, row: (typeof ROWS)[number]) => {
  const right = col + CARD_W;
  const top = row.y + SPLIT;
  const bottom = row.y + row.h;
  return `polygon(${x(col)} ${y(top)}, ${x(right)} ${y(top)}, ${x(right)} ${y(bottom - NOTCH - 1)}, ${x(
    right - NOTCH
  )} ${y(bottom)}, ${x(col)} ${y(bottom)})`;
};

/**
 * The layers a card is built from, in paint order. One table drives both the DOM and
 * the stylesheet, so a layer can never be styled as something it is not: `rest` is
 * what it sits at untouched, `active` what it animates to, and `strip` says which of
 * the two clip regions it belongs to.
 */
const LAYERS = [
  { cls: "tm-body", key: "Body", prop: "background-color", rest: IDLE.body, active: ACTIVE.body, strip: false, ink: false },
  { cls: "tm-strip", key: "Strip", prop: "background-color", rest: IDLE.strip, active: ACTIVE.strip, strip: true, ink: false },
  { cls: "tm-hatch", key: "Hatch", prop: "opacity", rest: 0, active: "1", strip: true, ink: false },
  { cls: "tm-icon", key: "Icon", prop: "background-color", rest: IDLE.icon, active: ACTIVE.icon, strip: false, ink: true },
  { cls: "tm-text", key: "Text", prop: "background-color", rest: IDLE.text, active: ACTIVE.text, strip: true, ink: true },
  { cls: "tm-accent", key: "Accent", prop: "background-color", rest: IDLE.accent, active: ACTIVE.icon, strip: false, ink: true },
] as const;

/**
 * A card the cursor only passes over: one beat, then rest for the remainder. Rest
 * comes from the layer's own `--tm-rest`, so these keyframes serve every layer
 * whichever colour — or, for the hatch, whichever opacity — it rests at. The card
 * is placed in the cycle by a negative delay rather than by its own keyframes.
 */
const hover = (name: string, prop: string, active: string) => `
@keyframes ${name} {
  0% { ${prop}: var(--tm-rest); }
  ${at(RISE)}, ${at(HOLD)} { ${prop}: ${active}; }
  ${at(FALL)}, 100% { ${prop}: var(--tm-rest); }
}`;

/**
 * A card that gets clicked. Same landing as a hover, but the value then holds to the
 * end of the cycle instead of falling back, so the pick stays lit while the cursor
 * moves on. Written on the absolute cycle — a pick cannot be a shifted copy of one
 * beat, because how long it holds depends on where in the pass it was chosen.
 */
const pick = (name: string, prop: string, active: string, land: number) => `
@keyframes ${name} {
  ${land > 0 ? `0%, ${at(land)}` : "0%"} { ${prop}: var(--tm-rest); }
  ${at(land + RISE)}, ${at(100 - RELEASE)} { ${prop}: ${active}; }
  100% { ${prop}: var(--tm-rest); }
}`;

/** The swell: identical for both kinds, but a pick settles back while staying red. */
const lift = (name: string, land: number) => `
@keyframes ${name} {
  ${land > 0 ? `0%, ${at(land)}` : "0%"} { transform: scale(1); }
  ${at(land + RISE)}, ${at(land + HOLD)} { transform: scale(1.13); }
  ${at(land + FALL)}, 100% { transform: scale(1); }
}`;

/** Pointer travel: parked on each card for its dwell, moving in between. */
const cursorMove = `
@keyframes tmCursorMove {
${STOPS.map((stop, i) => {
  const land = i * STEP;
  const left = x(COLS[stop.c] + CARD_W * 0.6);
  const top = y(ROWS[stop.r].y + ROWS[stop.r].h * 0.36);
  return `  ${at(land)}, ${at(land + HOLD)} { left: ${left}; top: ${top}; }`;
}).join("\n")}
  ${at(LAST)} { left: ${x(COLS[COLS.length - 1] + CARD_W * 0.6)}; top: ${y(
  ROWS[ROWS.length - 1].y + ROWS[ROWS.length - 1].h * 0.36
)}; }
  100% { left: ${x(COLS[0] + CARD_W * 0.6)}; top: ${y(ROWS[0].y + ROWS[0].h * 0.36)}; }
}`;

/**
 * The click: a quick dip on each of the three picks. Frames are collected into a map
 * first because the first pick lands on 0% and would otherwise emit that stop twice.
 */
const cursorClick = (() => {
  const frames = new Map<number, string>([
    [0, "scale(1)"],
    [100, "scale(1)"],
  ]);
  for (const row of ROWS.keys()) {
    const land = pickBeat(row) * STEP;
    frames.set(+land.toFixed(3), "scale(1)");
    frames.set(+(land + 0.9).toFixed(3), "scale(0.85)");
    frames.set(+(land + 2.4).toFixed(3), "scale(1)");
  }
  const body = [...frames]
    .sort((a, b) => a[0] - b[0])
    .map(([p, v]) => `  ${p}% { transform: ${v}; }`)
    .join("\n");
  return `\n@keyframes tmCursorClick {\n${body}\n}`;
})();

const CSS = `
.tm-card,
.tm-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  animation-duration: ${DURATION}s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
.tm-card { animation-name: tmHoverLift; }
${LAYERS.map((l) => `.${l.cls} { animation-name: tmHover${l.key}; }`).join("\n")}

.tm-wheel {
  animation-name: tmWheelTurn;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
@keyframes tmWheelTurn {
  0%, 5% { transform: rotate(calc(var(--tm-turn) * -1)); }
  45%, 55% { transform: rotate(var(--tm-turn)); }
  95%, 100% { transform: rotate(calc(var(--tm-turn) * -1)); }
}
/* The outer arc rocks from its drawn position to one side and back, never past it —
   see the note on ARC for why the other direction is off limits. */
.tm-wheel-arc {
  animation-name: tmArcSweep;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
@keyframes tmArcSweep {
  0%, 6% { transform: rotate(0deg); }
  46%, 56% { transform: rotate(var(--tm-turn)); }
  94%, 100% { transform: rotate(0deg); }
}

.tm-hatch {
  /* Sampled off the artwork: a lighter diagonal comb over the dark strip. */
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.075) 0 2px,
    transparent 2px 6px
  );
}
.tm-ink {
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}
.tm-icon,
.tm-text {
  -webkit-mask-image: url("${MASK}");
  mask-image: url("${MASK}");
}
.tm-accent {
  -webkit-mask-image: url("${ACCENT_MASK}");
  mask-image: url("${ACCENT_MASK}");
}

/* Picked cards run their own absolute timeline, so they take no delay. The doubled
   class keeps them ahead of the hover rules whatever order these blocks end up in. */
${ROWS.map(
  (_, r) => `.tm-card.tm-pick-r${r} { animation-name: tmPick${r}Lift; animation-delay: 0s; }
${LAYERS.map(
  (l) => `.tm-pick-r${r} .${l.cls} { animation-name: tmPick${r}${l.key}; animation-delay: 0s; }`
).join("\n")}`
).join("\n")}

${lift("tmHoverLift", 0)}
${LAYERS.map((l) => hover(`tmHover${l.key}`, l.prop, String(l.active))).join("\n")}
${ROWS.map((_, r) => {
  const land = pickBeat(r) * STEP;
  return [
    lift(`tmPick${r}Lift`, land),
    ...LAYERS.map((l) => pick(`tmPick${r}${l.key}`, l.prop, String(l.active), land)),
  ].join("\n");
}).join("\n")}

/* The pointer. Its tip is the SVG's own top-left corner, so left/top are the point
   being aimed at, and the arrow hangs down and to the right of it. It is a third of
   a card tall, so aiming at the upper half of the icon keeps the whole arrow inside
   the card without covering the label it is picking. */
.tm-cursor {
  position: absolute;
  width: 3%;
  aspect-ratio: 44.36 / 49.6;
  background-image: url("${CURSOR}");
  background-size: contain;
  background-repeat: no-repeat;
  pointer-events: none;
  /* The dip on a click pivots on the tip, which is the box's own top-left corner. */
  transform-origin: 0 0;
  /* The tight shadow is a rim: the pointer is white and crosses white line-art. */
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.85)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.55));
  animation: tmCursorMove ${DURATION}s ease-in-out infinite,
    tmCursorFade ${DURATION}s linear infinite,
    tmCursorClick ${DURATION}s ease-out infinite;
}
${cursorMove}
${cursorClick}
/* Gone for the tail, so the finished configuration is read without a pointer on it,
   and the long move back to the first card happens while nothing is visible. */
@keyframes tmCursorFade {
  0% { opacity: 0; }
  ${at(RISE)}, ${at(LAST)} { opacity: 1; }
  ${at(LAST + 2)}, 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .tm-card,
  .tm-layer { animation: none; }
  ${LAYERS.filter((l) => l.prop === "background-color")
    .map((l) => `.${l.cls}`)
    .join(",\n  ")} { background-color: var(--tm-rest); }
  .tm-hatch { opacity: var(--tm-rest); }
  /* The rings and the arc were lifted out in place, so parking them at rotate(0) puts
     the dial back exactly as the artwork drew it. */
  .tm-wheel,
  .tm-wheel-arc { animation: none; }
  /* No motion means no pass to watch, so leave the configuration made: the three
     picks chosen, which is how the artwork itself is drawn. */
${LAYERS.map(
  (l) => `  .tm-pick .${l.cls} { ${l.prop}: ${l.active}; }`
).join("\n")}
  .tm-cursor { display: none; }
}
`;

type Vars = React.CSSProperties & Record<string, string | number>;

const TailorMadeSelector = () => (
  <>
    {WHEEL_RINGS.map((ring) => (
      <Image
        key={ring.name}
        src={`/images/OTSecurity/cyber-range/tailor-made-wheel-${ring.name}.png`}
        alt=""
        aria-hidden="true"
        width={BOX.w}
        height={BOX.h}
        sizes="(max-width: 1024px) 90vw, 640px"
        className="tm-wheel pointer-events-none absolute inset-0 h-full w-full"
        style={
          {
            "--tm-turn": ring.turn,
            transformOrigin: WHEEL_ORIGIN,
            animationDuration: ring.dur,
            animationDelay: ring.delay,
          } as Vars
        }
      />
    ))}

    <Image
      src="/images/OTSecurity/cyber-range/tailor-made-wheel-arc.png"
      alt=""
      aria-hidden="true"
      width={BOX.w}
      height={BOX.h}
      sizes="(max-width: 1024px) 90vw, 640px"
      className="tm-wheel-arc pointer-events-none absolute inset-0 h-full w-full"
      style={
        {
          "--tm-turn": ARC.turn,
          transformOrigin: ARC.origin,
          animationDuration: ARC.dur,
          animationDelay: ARC.delay,
        } as Vars
      }
    />

    {STOPS.map((stop, i) => {
      const col = COLS[stop.c];
      const row = ROWS[stop.r];
      const picked = PICKS[stop.r] === stop.c;
      const clips = { body: bodyClip(col, row), strip: stripClip(col, row) };
      // A card the cursor merely passes over is one beat placed by a negative delay:
      // progress 0 lands on the frame the cursor arrives, which is `i` beats into the
      // cycle, so the delay holds it back by everything left after those beats.
      const delay = picked
        ? undefined
        : { animationDelay: `${(-DURATION * (1 - (i * STEP) / 100)).toFixed(3)}s` };
      return (
        <div
          key={`${stop.r}-${stop.c}`}
          className={picked ? `tm-card tm-pick tm-pick-r${stop.r}` : "tm-card"}
          aria-hidden="true"
          style={{
            ...delay,
            transformOrigin: `${x(col + CARD_W / 2)} ${y(row.y + row.h / 2)}`,
          }}
        >
          {LAYERS.map((l) => (
            <div
              key={l.cls}
              className={`tm-layer ${l.ink ? "tm-ink " : ""}${l.cls}`}
              style={
                {
                  ...delay,
                  clipPath: l.strip ? clips.strip : clips.body,
                  "--tm-rest": l.rest,
                } as Vars
              }
            />
          ))}
        </div>
      );
    })}

    <div className="tm-cursor" aria-hidden="true" />

    <style dangerouslySetInnerHTML={{ __html: CSS }} />
  </>
);

export default TailorMadeSelector;
